import { useState, useEffect, useCallback } from "react";
import { FiMonitor, FiCheck, FiX, FiRefreshCw, FiClock, FiAlertCircle } from "react-icons/fi";
import API_URL from "../config";

export default function Devices({ token }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState("");

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/devices/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) setDevices(Array.isArray(data.devices) ? data.devices : []);
      else setError(data.error || "Failed to load devices.");
    } catch {
      setError("Connection error.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  const handleApprove = async (deviceId) => {
    setActionId(deviceId);
    try {
      const res = await fetch(`${API_URL}/api/devices/${deviceId}/approve/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (res.ok) fetchDevices();
      else { const d = await res.json(); setError(d.error || "Failed to approve device."); }
    } catch { setError("Connection error."); }
    finally { setActionId(null); }
  };

  const handleRevoke = async (deviceId) => {
    if (!window.confirm("Revoke this device? It will no longer be able to clock in.")) return;
    setActionId(deviceId);
    try {
      const res = await fetch(`${API_URL}/api/devices/${deviceId}/revoke/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (res.ok) fetchDevices();
      else { const d = await res.json(); setError(d.error || "Failed to revoke device."); }
    } catch { setError("Connection error."); }
    finally { setActionId(null); }
  };

  const filtered = devices.filter((d) => {
    if (filter === "pending") return !d.is_active;
    if (filter === "active") return d.is_active;
    return true;
  });

  const pendingCount = devices.filter((d) => !d.is_active).length;

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center">
          <FiMonitor size={18} className="text-blue-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Devices
            {pendingCount > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-amber-100 text-amber-700 border border-amber-200 rounded-full font-medium">
                {pendingCount} pending
              </span>
            )}
          </h2>
          <p className="text-slate-500 text-sm">Approve or revoke kiosk devices for your business</p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
          <FiAlertCircle size={15} className="text-red-500 flex-shrink-0" />
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {["all", "pending", "active"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors capitalize ${
                filter === f ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >{f}</button>
          ))}
        </div>
        <button onClick={fetchDevices} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 border border-slate-300 rounded-lg bg-white hover:bg-slate-50 transition-colors disabled:opacity-40"
        >
          <FiRefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <FiMonitor size={28} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">
              {filter === "pending" ? "No devices awaiting approval." : "No devices found."}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Device ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">First Used By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Last Seen</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((device) => (
                <tr key={device.device_id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs text-slate-700 bg-slate-100 px-2 py-1 rounded">
                      {device.device_id.substring(0, 12)}…
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {device.is_active ? (
                      <span className="flex items-center gap-1.5 text-emerald-700 text-sm font-medium"><FiCheck size={13} /> Active</span>
                    ) : (
                      <span className="flex items-center gap-1.5 text-amber-700 text-sm font-medium"><FiClock size={13} /> Pending</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{device.employee || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-3 text-sm text-slate-500">
                    {device.last_seen
                      ? new Date(device.last_seen).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {device.is_active ? (
                      <button onClick={() => handleRevoke(device.device_id)} disabled={actionId === device.device_id}
                        className="flex items-center gap-1 mx-auto px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-40 transition-colors"
                      ><FiX size={12} /> Revoke</button>
                    ) : (
                      <button onClick={() => handleApprove(device.device_id)} disabled={actionId === device.device_id}
                        className="flex items-center gap-1 mx-auto px-3 py-1.5 text-xs font-medium text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-50 disabled:opacity-40 transition-colors"
                      ><FiCheck size={12} /> Approve</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-slate-400 text-xs text-center mt-4">
        Pending devices tried to clock in from an unrecognized device. Approve to allow, or ignore to block.
      </p>
    </div>
  );
}
